import { useQuery } from '@tanstack/react-query';

import { api } from './api';

export interface TeamPerson {
  id: string;
  name: string;
  seconds: number;
  daysLogged: number;
  missingDates: string[];
  projects: { name: string; seconds: number }[];
  hasContract: boolean;
  /** Deactivated since, but worked in this period. */
  left: boolean;
}

export interface TeamProject {
  id: string;
  name: string;
  customer: string;
  seconds: number;
  people: { name: string; seconds: number }[];
}

export interface TeamOverview {
  from: string;
  to: string;
  /** How far the missing-days column was checked; not always `to`. */
  owedTo: string;
  people: TeamPerson[];
  projects: TeamProject[];
}

/** One request behind all three views - they share the same five queries. */
export const useTeamOverview = (from: string, to: string) =>
  useQuery({
    queryKey: ['team', from, to],
    queryFn: () => api.get<TeamOverview>(`/reporting/team?from=${from}&to=${to}`),
  });

export interface YearGrid {
  year: number;
  /** The twelve columns as `YYYY-MM`, in the order the figures are bucketed. */
  months: string[];
  /** How to name the year on screen: `2026-27` when it starts in April. */
  label: string;
  people: { id: string; name: string; left: boolean; months: number[]; total: number }[];
  monthTotals: number[];
}

/** `null` asks for whichever financial year is current - only the API knows. */
export const useTeamYear = (year: number | null) =>
  useQuery({
    queryKey: ['team', 'year', year],
    queryFn: () => api.get<YearGrid>(`/reporting/team/year${year === null ? '' : `?year=${year}`}`),
  });

export interface InactiveProjects {
  sinceDays: number;
  projects: {
    id: string;
    name: string;
    customer: string;
    entries: number;
    lastBooked: string | null;
    endedOn: string | null;
  }[];
}

export const useInactiveProjects = (days: number) =>
  useQuery({
    queryKey: ['team', 'inactive', days],
    queryFn: () => api.get<InactiveProjects>(`/reporting/projects/inactive?days=${days}`),
  });

export interface ProjectYearGrid {
  year: number;
  months: string[];
  label: string;
  projects: {
    id: string;
    name: string;
    customer: string;
    /** Not visible in the catalogue any more, but it holds hours somebody worked. */
    hidden: boolean;
    months: number[];
    total: number;
  }[];
  monthTotals: number[];
}

export const useProjectYear = (year: number | null, enabled = true) =>
  useQuery({
    queryKey: ['team', 'project-year', year],
    queryFn: () =>
      api.get<ProjectYearGrid>(
        `/reporting/projects/year${year === null ? '' : `?year=${year}`}`,
      ),
    enabled,
  });

export interface OvertimeReport {
  year: number;
  months: string[];
  label: string;
  /** Days after this are not counted: they have not happened. */
  asOf: string;
  people: {
    id: string;
    name: string;
    left: boolean;
    hasContract: boolean;
    expected: number[];
    actual: number[];
    balance: number[];
    totalExpected: number;
    totalActual: number;
    totalBalance: number;
  }[];
}

export const useOvertime = (year: number | null) =>
  useQuery({
    queryKey: ['team', 'overtime', year],
    queryFn: () =>
      api.get<OvertimeReport>(`/reporting/overtime${year === null ? '' : `?year=${year}`}`),
  });

/** One person's billable time against the time they were available to work. */
export interface UtilisationRow {
  id: string;
  name: string;
  left: boolean;
  hasContract: boolean;
  capacitySeconds: number;
  recordedSeconds: number;
  billableSeconds: number;
  nonBillableSeconds: number;
  /** Paise, as a string. Never a number - that is how a money column loses paise. */
  billableValueMinor: string;
  /** null, not 0, where there is no contract: "no target" is not "0%". */
  utilisation: number | null;
  recordedRatio: number | null;
  billableShare: number | null;
}

export interface Utilisation {
  from: string;
  to: string;
  /** Capacity is counted to here, which is today when the period is still running. */
  countedTo: string;
  /** How many visible projects carry the billable flag. See the warning banner. */
  flags: { projectsTotal: number; projectsBillable: number };
  rows: UtilisationRow[];
  totals: {
    capacitySeconds: number;
    recordedSeconds: number;
    billableSeconds: number;
    billableValueMinor: string;
    utilisation: number | null;
  };
}

export const useUtilisation = (from: string, to: string) =>
  useQuery({
    queryKey: ['working-time', 'utilisation', from, to],
    queryFn: () => api.get<Utilisation>(`/reporting/team/utilisation?from=${from}&to=${to}`),
  });
